import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, ShieldCheck, Download, Info, MessageCircle } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Reveal } from "./Reveal";
import { LegalDialog } from "./LegalDialog";
import { useLang } from "@/i18n";
import { isOpenNow } from "@/lib/hours";

const BigLink = ({ href, label, value, testid, icon: Icon, info, highlight }) => (
  <a
    href={href}
    data-testid={testid}
    className="group relative block border-b border-[var(--line)] py-7 md:py-9"
  >
    {highlight && (
      <motion.span
        aria-hidden
        className="absolute left-0 top-2 bottom-2 w-[2px] bg-[var(--accent)] origin-top"
        initial={{ scaleY: 0, opacity: 0 }}
        animate={{ scaleY: [0, 1, 1, 0], opacity: [0, 1, 1, 0] }}
        transition={{ duration: 2.4, times: [0, 0.12, 0.82, 1], ease: "easeInOut" }}
      />
    )}
    <div className="flex items-center gap-3 mb-2">
      <motion.span
        className="inline-flex"
        animate={highlight ? { scale: [1, 1.28, 1] } : {}}
        transition={{ duration: 1, ease: "easeInOut", repeat: highlight ? 1 : 0 }}
      >
        <Icon className="h-4 w-4 text-[var(--accent)]" strokeWidth={1.4} />
      </motion.span>
      <span className="font-mono2 text-[10px] tracking-[0.35em] uppercase text-[var(--txt-mut)]">
        {label}
      </span>
      {info && (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                role="button"
                tabIndex={0}
                aria-label="Info"
                data-testid="pec-info"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-[var(--line)] text-[var(--txt-mut)] hover:text-[var(--txt)] hover:border-[var(--accent)] transition-colors"
              >
                <Info className="h-2.5 w-2.5" strokeWidth={2} />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs bg-[var(--bg-2)] border border-[var(--line)] text-[var(--txt-dim)] font-body font-light text-xs leading-relaxed">
              {info}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
    <motion.span
      className="font-heading font-bold text-2xl md:text-4xl lg:text-5xl tracking-tight text-[var(--txt)] group-hover:text-[var(--accent)] group-hover:translate-x-2 inline-block transition-[color,transform] duration-500 break-all"
      animate={highlight ? { x: [0, 8, 0] } : {}}
      transition={{ duration: 1.2, ease: "easeInOut" }}
    >
      {value}
    </motion.span>
  </a>
);

export const Contact = () => {
  const { t, lang } = useLang();
  const c = t.contact;
  const l = t.location;
  const sectionRef = useRef(null);
  const [spotlight, setSpotlight] = useState(false);
  const [open, setOpen] = useState(() => isOpenNow());

  // Keep the live open/closed status accurate while the page is open.
  useEffect(() => {
    const id = setInterval(() => setOpen(isOpenNow()), 60000);
    return () => clearInterval(id);
  }, []);

  // When the contact section first scrolls into view, briefly highlight
  // the phone link and signal the WhatsApp button to pulse.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          io.disconnect();
          setSpotlight(true);
          window.dispatchEvent(new Event("ag:contact-visible"));
          setTimeout(() => setSpotlight(false), 2600);
        }
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const vcardText = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    "N:Gasteiger;Achim;;;",
    "FN:Achim Gasteiger",
    "ORG:Achim Gasteiger Architektur",
    `TITLE:${lang === "it" ? "Architetto (Arch. Junior)" : "Architekt (Arch. Junior)"}`,
    "TEL;TYPE=CELL,VOICE:+393397966579",
    "EMAIL;TYPE=INTERNET:arch.gasteiger@gmail.com",
    "EMAIL;TYPE=INTERNET,PEC:achim.gasteiger@archiworldpec.it",
    "ADR;TYPE=WORK:;;Geizkoflerstraße 5;Sterzing;BZ;39049;Italia",
    "END:VCARD",
  ].join("\r\n");

  const saveVCard = () => {
    const blob = new Blob([vcardText], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Achim_Gasteiger.vcf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Pre-filled appointment request via e-mail or WhatsApp.
  const bookMailHref = `mailto:arch.gasteiger@gmail.com?subject=${encodeURIComponent(
    c.bookSubject,
  )}&body=${encodeURIComponent(c.bookMsg)}`;
  const bookWaHref = `https://wa.me/393397966579?text=${encodeURIComponent(c.bookMsg)}`;

  return (
    <section
      id="kontakt"
      ref={sectionRef}
      className="relative border-t border-[var(--line)] bg-[var(--bg-1)] pt-24 md:pt-36 pb-12"
      data-testid="contact-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Reveal>
          <span className="font-mono2 text-[11px] tracking-[0.4em] uppercase text-[var(--txt-mut)]">
            {c.label}
          </span>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-heading font-bold tracking-tight text-4xl md:text-6xl lg:text-7xl mt-6 leading-[0.95] max-w-3xl">
            {c.headingLines[0]}
            <br />
            <span className="text-[var(--accent)]">{c.headingLines[1]}</span>
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <button
            onClick={saveVCard}
            data-testid="save-contact"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[var(--accent)] text-black hover:bg-white px-6 py-3 font-mono2 text-[11px] tracking-[0.25em] uppercase transition-colors duration-300 active:scale-95"
          >
            <Download className="h-4 w-4" strokeWidth={1.6} />
            {c.saveContact}
          </button>
        </Reveal>
        <Reveal delay={0.24}>
          <div
            className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono2 text-[10px] tracking-[0.25em] uppercase text-[var(--txt-mut)]"
            data-testid="contact-booking"
          >
            <span>{c.bookLabel}</span>
            <a
              href={bookMailHref}
              data-testid="book-email"
              className="inline-flex items-center gap-1.5 text-[var(--txt-dim)] hover:text-[var(--accent)] transition-colors duration-300 link-underline"
            >
              <Mail className="h-3.5 w-3.5" strokeWidth={1.6} />
              {c.bookEmail}
            </a>
            <a
              href={bookWaHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="book-whatsapp"
              className="inline-flex items-center gap-1.5 text-[var(--txt-dim)] hover:text-[var(--accent)] transition-colors duration-300 link-underline"
            >
              <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.6} />
              {c.bookWhatsapp}
            </a>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mt-14 md:mt-20">
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <BigLink
                href="tel:+393397966579"
                label={c.phoneLabel}
                value="+39 339 796 6579"
                testid="contact-phone"
                icon={Phone}
                highlight={spotlight}
              />
            </Reveal>
            <Reveal delay={0.15}>
              <BigLink
                href="mailto:arch.gasteiger@gmail.com"
                label={c.emailLabel}
                value="arch.gasteiger@gmail.com"
                testid="contact-email"
                icon={Mail}
              />
            </Reveal>
            <Reveal delay={0.2}>
              <BigLink
                href="mailto:achim.gasteiger@archiworldpec.it"
                label={c.pecLabel}
                value="achim.gasteiger@archiworldpec.it"
                testid="contact-pec"
                icon={ShieldCheck}
                info={c.pecInfo}
              />
            </Reveal>
          </div>

          <div className="lg:col-span-4 lg:pt-4">
            <Reveal delay={0.2}>
              <div className="font-mono2 text-sm text-[var(--txt-dim)] leading-loose space-y-6">
                <div>
                  <div className="text-[var(--txt-mut)] text-[10px] tracking-[0.3em] uppercase mb-2">
                    {c.officeLabel}
                  </div>
                  {l.addressLines[0]}
                  <br />
                  {l.addressLines[1]}
                </div>
                <div>
                  <div className="text-[var(--txt-mut)] text-[10px] tracking-[0.3em] uppercase mb-2">
                    {c.taxLabel}
                  </div>
                  GSTCHM92C06M067B
                </div>
                <div>
                  <div className="text-[var(--txt-mut)] text-[10px] tracking-[0.3em] uppercase mb-2">
                    {c.vatLabel}
                  </div>
                  03280740212
                </div>
                <div>
                  <div className="text-[var(--txt-mut)] text-[10px] tracking-[0.3em] uppercase mb-2">
                    {c.sdiLabel}
                  </div>
                  ZS100U1
                </div>
                <div>
                  <div className="text-[var(--txt-mut)] text-[10px] tracking-[0.3em] uppercase mb-2">
                    {c.chamberLabel}
                  </div>
                  {c.chamberValue}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-10 flex items-start gap-4" data-testid="contact-qr">
                <button
                  type="button"
                  onClick={saveVCard}
                  aria-label={c.saveContact}
                  data-testid="contact-qr-download"
                  className="bg-white p-2 rounded-md shrink-0 transition-transform duration-300 hover:scale-[1.04] active:scale-95 cursor-pointer"
                >
                  <QRCodeSVG value={vcardText} size={92} bgColor="#ffffff" fgColor="#0a0a0a" level="M" />
                </button>
                <div className="flex flex-col gap-3 pt-0.5">
                  <span
                    className="inline-flex items-center gap-2 font-mono2 text-[10px] tracking-[0.2em] uppercase"
                    data-testid="contact-open-status"
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        open ? "bg-emerald-400 animate-pulse" : "bg-[var(--txt-mut)]"
                      }`}
                    />
                    <span className={open ? "text-emerald-300" : "text-[var(--txt-mut)]"}>
                      {open ? l.openNow : l.closedNow}
                    </span>
                  </span>
                  <div className="font-mono2 text-[10px] leading-relaxed text-[var(--txt-dim)]">
                    <div className="text-[var(--txt-mut)] tracking-[0.2em] uppercase mb-1">
                      {l.hoursLabel}
                    </div>
                    {l.hours}
                  </div>
                  <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-[var(--txt-mut)] leading-relaxed max-w-[150px]">
                    {c.qrHint}
                  </span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-24 md:mt-32 pt-8 border-t border-[var(--line)] flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <span className="font-heading font-extrabold tracking-tight text-lg">
            ACHIM GASTEIGER
            <span className="font-mono2 font-light text-[var(--txt-dim)] text-[10px] ml-2 tracking-[0.3em]">
              ARCHITEKTUR
            </span>
          </span>
          <div className="flex items-center gap-6">
            <a
              href="https://www.google.com/maps?cid=845525002029875784"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-profile"
              className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[var(--txt-mut)] hover:text-[var(--txt)] transition-colors duration-300 link-underline"
            >
              {c.googleProfile}
            </a>
            <LegalDialog type="impressum" />
            <LegalDialog type="datenschutz" />
          </div>
          <span className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[var(--txt-mut)]">
            © {new Date().getFullYear()} — {c.copyright}
          </span>
        </div>
      </div>
    </section>
  );
};
